import { QuestionCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { isNil } from 'lodash'

import { createDictApi } from '@eastcoal/api/system/dict/createDict'
import { queryDictApi } from '@eastcoal/api/system/dict/queryDict'
import { updateDictApi } from '@eastcoal/api/system/dict/updateDict'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'
import { mapNumberEnumOptions } from '@eastcoal/common/utils/enums'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { EditableTable } from '@eastcoal/formily/components/EditableTable'
import { AddButton } from '@eastcoal/formily/components/EditableTable/components/AddButton'
import { RemoveButton } from '@eastcoal/formily/components/EditableTable/components/RemoveButton'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { Select } from '@eastcoal/formily/components/Select'
import { Switch } from '@eastcoal/formily/components/Switch'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { BizTypeTextMap } from '@eastcoal/shared/enums/BizType'

import { DICT_LIST_PAGE_PATH } from '../constants/path'

import { DictFormData, DictFormExposedProps, DictFormProps } from './interface'

const schema: DictFormProps['schema'] = [
  {
    type: 'string',
    name: 'code',
    title: (
      <Tooltip title='字母会自动转成大写形式'>
        <span>编码&nbsp;</span>
        <QuestionCircleOutlined />
      </Tooltip>
    ),
    'x-component': Input,
    'x-decorator': FormItem,
    'x-validator': {
      required: true,
      pattern: '[_a-zA-Z]',
      message: '只能输入下划线（_）与字母'
    }
  },
  {
    name: 'name',
    title: '名称',
    type: 'string',
    required: true,
    'x-decorator': FormItem,
    'x-component': Input
  },
  {
    name: 'tag',
    title: (
      <Tooltip title='用于标识属于哪个模块，比如物料管理、客户管理等等'>
        <span>标签&nbsp;</span>
        <QuestionCircleOutlined />
      </Tooltip>
    ),
    type: 'string',
    'x-decorator': FormItem,
    'x-component': Select,
    enum: mapNumberEnumOptions(BizTypeTextMap)
  },
  {
    name: 'comment',
    title: '备注',
    type: 'string',
    'x-decorator': FormItem,
    'x-component': Input
  },
  {
    type: 'array',
    name: 'itemEntityList',
    title: '字典项',
    'x-component': EditableTable,
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 800
    },
    items: [
      {
        type: 'string',
        name: 'val',
        title: (
          <Tooltip title='字典下拉框的选择项'>
            <span>值&nbsp;</span>
            <QuestionCircleOutlined />
          </Tooltip>
        ),
        'x-component': Input,
        'x-decorator': FormItem,
        'x-decorator-props': {
          wrapperWidth: 'auto'
        },
        'x-validator': [
          {
            required: true,
            message: '值不能为空！'
          }
        ]
      },
      {
        type: 'string',
        name: 'label',
        title: (
          <Tooltip title='也可以用于当做附加信息展示在【值】后面'>
            <span>文本说明&nbsp;</span>
            <QuestionCircleOutlined />
          </Tooltip>
        ),
        'x-component': Input,
        'x-column-props': {
          width: 240
        }
      },
      {
        type: 'boolean',
        name: 'enable',
        title: '是否可用',
        default: true,
        'x-component': Switch,
        'x-column-props': {
          width: 160
        }
      },
      {
        name: 'action',
        type: 'void',
        title: <AddButton />,
        'x-display': '{{ $form.editable ? "visible" : "none" }}',
        'x-component': RemoveButton,
        'x-column-props': {
          width: 120,
          align: 'center'
        }
      }
    ]
  }
]

const readApi: DictFormProps['readApi'] = ({ id }) => {
  return {
    api: () => queryDictApi({ id }),
    ready: !isNil(id),
    refreshDeps: [id]
  }
}

const formatterData = (values: DictFormData) => {
  return {
    ...values,
    code: values.code?.toLocaleUpperCase()
  }
}

const createApi: DictFormProps['createApi'] = values => {
  return createDictApi(formatterData(values))
}

const updateApi: DictFormProps['createApi'] = values => {
  return updateDictApi(formatterData(values))
}

export const DictForm = ({ mode }: DictFormExposedProps) => {
  const navigator = useNavigate()
  return (
    <PageContainer>
      <CrudForm
        createApi={createApi}
        mode={mode}
        name='dictForm'
        readApi={readApi}
        schema={schema}
        updateApi={updateApi}
        actions={[
          <BackButton key='backButton' />,
          <SaveButton key='saveButton' />
        ]}
        layout={{
          labelWidth: 100,
          wrapperWidth: 400
        }}
        onSuccess={() => navigator(DICT_LIST_PAGE_PATH)}
      />
    </PageContainer>
  )
}
